import type { AdMedium } from "./ad-medium";

export type SuggestedActionId =
  | "riff"
  | "refresh"
  | "convert_to_motion"
  | "remix_in_style"
  | "resize"
  | "cut_down"
  | "create_statics";

export type SavedStyle = { id: string; name: string };

export const SUGGESTED_ACTIONS = {
  riff: {
    id: "riff",
    label: "Riff on this ad",
    projectPrefix: "Riff",
    placeholder: "Describe the riff you want on this creative…",
  },
  refresh: {
    id: "refresh",
    label: "Refresh this ad",
    projectPrefix: "Refresh",
    placeholder: "Describe the refresh you want for this creative…",
  },
  convert_to_motion: {
    id: "convert_to_motion",
    label: "Convert to motion",
    projectPrefix: "Motion",
    placeholder: "Describe how this creative should move…",
  },
  remix_in_style: {
    id: "remix_in_style",
    label: "Remix in a new style",
    projectPrefix: "Remix",
    placeholder: "Describe the remix you want for this creative…",
  },
  resize: {
    id: "resize",
    label: "Resize creative",
    projectPrefix: "Resize",
    placeholder: "Describe the placements you need this creative for…",
  },
  cut_down: {
    id: "cut_down",
    label: "Cut down or remix",
    projectPrefix: "Cut down",
    placeholder: "Describe the cut you want from this creative…",
  },
  create_statics: {
    id: "create_statics",
    label: "Create static variants",
    projectPrefix: "Statics",
    placeholder: "Describe the statics you want pulled from this video…",
  },
} as const satisfies {
  [K in SuggestedActionId]: {
    id: K;
    label: string;
    projectPrefix: string;
    placeholder: string;
  };
};

export type SuggestedAction =
  | (typeof SUGGESTED_ACTIONS)[Exclude<SuggestedActionId, "remix_in_style">]
  | ((typeof SUGGESTED_ACTIONS)["remix_in_style"] & { style: SavedStyle });

type BuildArgs = {
  adId: string;
  format: AdMedium;
  fatigue: "fatiguing" | "not-fatiguing";
  savedStyles: SavedStyle[];
};

// The opener follows the ad's fatigue, not the tab it was opened from.
export function buildSuggestedActions({
  adId,
  format,
  fatigue,
  savedStyles,
}: BuildArgs): SuggestedAction[] {
  const opener =
    SUGGESTED_ACTIONS[fatigue === "fatiguing" ? "refresh" : "riff"];

  if (format === "video") {
    return [
      opener,
      SUGGESTED_ACTIONS.cut_down,
      SUGGESTED_ACTIONS.create_statics,
    ];
  }

  const [firstStyle, ...restStyles] = savedStyles;
  return [
    opener,
    SUGGESTED_ACTIONS.convert_to_motion,
    firstStyle === undefined
      ? SUGGESTED_ACTIONS.resize
      : {
          ...SUGGESTED_ACTIONS.remix_in_style,
          style: pickRemixStyle(adId, [firstStyle, ...restStyles]),
        },
  ];
}

// Keyed on the ad id, not randomized: an ad keeps its remix across renders
// and reopens while different ads spread across the brand's saved styles.
function pickRemixStyle(
  adId: string,
  savedStyles: [SavedStyle, ...SavedStyle[]],
): SavedStyle {
  return savedStyles[hashAdId(adId) % savedStyles.length] ?? savedStyles[0];
}

function hashAdId(adId: string): number {
  let hash = 0;
  for (let i = 0; i < adId.length; i++) {
    hash = (hash * 31 + adId.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}
